import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { CreateOrgWizard } from '@/features/org/components/CreateOrgWizard'
import { useSwitchOrg, useOpenCreatedOrg } from '@/lib/useSwitchOrg'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'

export function OrgWorkspaceSwitcher() {
  const { selectedOrg, selectedSlug, orgsLoading, orgs } = useSelectedOrg()
  const { collapsed } = useSidebar()
  const navigate = useNavigate()
  const switchOrg = useSwitchOrg()
  const openCreatedOrg = useOpenCreatedOrg()
  const [open, setOpen] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null)
  const [switcherHovered, setSwitcherHovered] = useState(false)
  const [settingsHovered, setSettingsHovered] = useState(false)
  const [newOrgHovered, setNewOrgHovered] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const orgList = [...orgs].sort((a, b) => (a.slug ?? '').localeCompare(b.slug ?? ''))

  if (orgsLoading && !selectedOrg) {
    return (
      <div style={styles.row}>
        <div style={styles.skeleton} />
      </div>
    )
  }

  return (
    <>
      {showCreate && (
        <CreateOrgWizard
          onClose={() => setShowCreate(false)}
          onCreated={async (org) => {
            setShowCreate(false)
            // Refetches the org list, THEN navigates so OrgLayout accepts the new
            // slug (calling setSelectedSlug alone would be reverted to match the URL).
            await openCreatedOrg(org)
          }}
        />
      )}
      <div style={{ ...styles.row, ...(collapsed ? { justifyContent: 'center' } : {}) }} ref={ref} data-testid="org-switcher">
        {collapsed ? (
          <Tooltip text={selectedOrg?.slug ? `Org: ${selectedOrg.slug}` : 'Select org'} placement="right" delayMs={120}>
            <button
              style={{ ...styles.avatar, ...(switcherHovered || open ? styles.avatarHover : {}) }}
              aria-label={selectedOrg?.slug ? `Org: ${selectedOrg.slug}. Switch organisation` : 'Select organisation'}
              onClick={() => setOpen((o) => !o)}
              onMouseEnter={() => setSwitcherHovered(true)}
              onMouseLeave={() => setSwitcherHovered(false)}
            >
              {(selectedOrg?.slug ?? '?')[0]?.toUpperCase()}
            </button>
          </Tooltip>
        ) : (
          <>
            <button
              style={{
                ...styles.switcherBtn,
                ...(switcherHovered || open ? styles.switcherBtnHover : {}),
              }}
              onClick={() => setOpen((o) => !o)}
              onMouseEnter={() => setSwitcherHovered(true)}
              onMouseLeave={() => setSwitcherHovered(false)}
            >
              <span style={styles.orgName}>{selectedOrg?.slug ?? '…'}</span>
              <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}>▾</span>
            </button>
            <Tooltip text="Org settings - members, billing & danger zone" placement="right" delayMs={120}>
              <button
                style={{
                  ...styles.settingsBtn,
                  ...(settingsHovered ? styles.settingsBtnHover : {}),
                }}
                aria-label="Org settings"
                onClick={() => selectedSlug && navigate(`/org/${selectedSlug}/settings`)}
                onMouseEnter={() => setSettingsHovered(true)}
                onMouseLeave={() => setSettingsHovered(false)}
              >
                ⚙
              </button>
            </Tooltip>
          </>
        )}
        {open && (
          <div style={{ ...styles.popover, ...(collapsed ? styles.popoverCollapsed : {}) }}>
            {orgList.map((o) => (
              <button
                key={o.slug}
                style={{
                  ...styles.popoverItem,
                  ...(o.slug === selectedSlug ? styles.popoverItemActive : {}),
                  ...(hoveredSlug === o.slug && o.slug !== selectedSlug ? styles.popoverItemHover : {}),
                }}
                onClick={() => { setOpen(false); switchOrg(o.slug!) }}
                onMouseEnter={() => setHoveredSlug(o.slug ?? null)}
                onMouseLeave={() => setHoveredSlug(null)}
              >
                <span style={styles.popoverSlug}>{o.slug}</span>
                {o.is_demo === 1 && (
                  <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: '0.6rem' }}>demo</span>
                )}
                <span style={styles.roleBadge}>{o.role}</span>
              </button>
            ))}
            <div style={styles.popoverDivider} />
            <button
              style={{
                ...styles.popoverItem,
                ...(newOrgHovered ? styles.popoverItemHover : {}),
              }}
              onClick={() => { setOpen(false); setShowCreate(true) }}
              onMouseEnter={() => setNewOrgHovered(true)}
              onMouseLeave={() => setNewOrgHovered(false)}
            >
              <span style={{ ...styles.popoverSlug, color: 'var(--accent)' }}>+ New org</span>
            </button>
          </div>
        )}
      </div>
    </>
  )
}

const styles: Record<string, React.CSSProperties> = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '10px 10px',
    borderBottom: '1px solid var(--border)',
    position: 'relative',
  },
  skeleton: {
    height: 28,
    borderRadius: 5,
    background: 'var(--bg3)',
    flex: 1,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 7,
    border: '1px solid var(--border)',
    background: 'var(--bg3)',
    color: 'var(--text)',
    fontFamily: 'var(--head)',
    fontWeight: 700,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'border-color 0.15s, background 0.15s',
  },
  avatarHover: {
    borderColor: 'var(--accent)',
    background: 'var(--bg2)',
  },
  popoverCollapsed: {
    left: '100%',
    right: 'auto',
    top: 0,
    marginLeft: 8,
    minWidth: 200,
  },
  switcherBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 8px',
    borderRadius: 5,
    border: '1px solid var(--border)',
    background: 'var(--bg3)',
    cursor: 'pointer',
    minWidth: 0,
    transition: 'border-color 0.15s, background 0.15s',
  },
  switcherBtnHover: {
    borderColor: 'var(--accent)',
    background: 'var(--bg2)',
  },
  orgName: {
    fontFamily: 'var(--mono)',
    fontSize: '0.72rem',
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'left',
  },
  chevron: {
    fontSize: '0.6rem',
    color: 'var(--text3)',
    flexShrink: 0,
  },
  settingsBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text3)',
    fontSize: '1.3rem',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'border-color 0.15s, color 0.15s, background 0.15s',
  },
  settingsBtnHover: {
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
    background: 'var(--accent-d)',
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 10,
    right: 10,
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    zIndex: 'var(--z-overlay)',
    overflow: 'hidden',
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '8px 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    gap: 8,
    transition: 'background 0.1s',
  },
  popoverItemActive: {
    background: 'var(--accent-d)',
  },
  popoverItemHover: {
    background: 'var(--bg3)',
  },
  popoverSlug: {
    fontFamily: 'var(--mono)',
    fontSize: '0.72rem',
    color: 'var(--text)',
  },
  roleBadge: {
    fontFamily: 'var(--mono)',
    fontSize: '0.6rem',
    color: 'var(--text3)',
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 3,
    padding: '1px 5px',
  },
  popoverDivider: {
    height: 1,
    background: 'var(--border)',
    margin: '2px 0',
  },
}
